import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useFormik } from "formik";
import { ReactComponent as InfoIcon1 } from "../../../Assets/Icons/info.svg";
import { ReactComponent as WhitePlainTick } from "../../../Assets/Icons/whiteplaintick.svg";
import { Tooltip } from "antd";
import { ReactComponent as BlueCrossIcon } from "../../../Assets/Icons/blueCross.svg";
import { rulesOfferSchema } from "../../../utils/validationSchemas";
import Input from "../FormInputs";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentBusiness } from "../../../redux/slices/merchantSlice";
import {
  createOffer,
  createRule,
  deleteRule,
  getRules,
  getGroupedRules,
} from "../../../services/offerService";
import declinedIcon from "../../../Assets/Icons/declinedIcon.svg";
import pendingIcon from "../../../Assets/Icons/pendingIcon.svg";
import { ReactComponent as CustomRulesIcon } from "../../../Assets/Icons/customrules&regulationsicon.svg";
import { useOfferFormContext } from "./OfferFormContext";
import "./offerform.scss";
import { scrollToFirstError } from "../../../utils/utilFunctions";
import { getS3PublicUrl } from "../../../utils/s3";

const uniqueByTitle = (arr) => {
  if (!Array.isArray(arr)) return [];
  const seen = new Set();
  const out = [];
  for (const r of arr) {
    const key = String(r?.title || "").trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
};

// Move sub-components out to prevent re-mounting issues
const RuleOption = ({ rule, isChecked, onCheck, statusIcon }) => {
  const hasBullets = rule.title?.includes('•');

  const renderRuleText = () => {
    if (!hasBullets) return <span className="rule-label">{rule.title}</span>;

    // Split by bullet point
    const parts = rule.title.split('•').map(p => p.trim()).filter(Boolean);
    const mainText = parts[0];
    const subRules = parts.slice(1);

    return (
      <div className="rule-label-complex">
        <span className="main-text">{mainText}</span>
        <ul className="rule-sub-list">
          {subRules.map((subRule, idx) => (
            <li key={idx} className="sub-rule-item">
              {subRule}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div
      className={`rule-item-container ${isChecked ? 'selected' : ''} ${hasBullets ? 'has-bullets' : ''}`}
      onClick={() => onCheck(rule)}
    >
      <div className="rule-checkbox">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 6L9 17L4 12" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      {renderRuleText()}
      {statusIcon && <img src={statusIcon} className="w-4 h-4 ml-1 mt-1" alt="" />}
    </div>
  );
};

const getIconUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;

  // System icons like /uploads/... are often at the S3 bucket root, 
  // not inside the dirName (dev-data) folder.
  if (path.startsWith("/uploads") || path.startsWith("uploads")) {
    const bucket = process.env.REACT_APP_BUCKET_NAME;
    const region = process.env.REACT_APP_REGION;
    if (bucket && region) {
      const cleanPath = path.startsWith("/") ? path : `/${path}`;
      return `https://${bucket}.s3.${region}.amazonaws.com${cleanPath}`;
    }
  }

  return getS3PublicUrl(path);
};

const GroupCard = ({ id, title, rules, selectedRulesIds, onCheck, isExpanded, onToggle, icon }) => {
  // Balanced sorting for columns: items fill Column 1 then Column 2 (Figma style)
  const sortedRules = useMemo(() => {
    if (!rules || rules.length === 0) return [];
    return [...rules];
  }, [rules]);

  if (!rules || rules.length === 0) return null;

  const midPoint = Math.ceil(sortedRules.length / 2);
  const leftRules = sortedRules.slice(0, midPoint);
  const rightRules = sortedRules.slice(midPoint);

  return (
    <div className={`rules-group-card ${isExpanded ? 'is-expanded' : ''}`}>
      <div className="group-header" onClick={() => onToggle(id)}>
        <div className="header-left">
          {icon && (
            <div className="group-icon-wrapper">
              <img
                src={getIconUrl(icon)}
                alt=""
                className="group-cat-icon"
                onError={(e) => {
                  console.error("Icon failed to load:", e.target.src);
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
          <span className="group-title">{title}</span>
        </div>
        <div className="header-right">
          <svg
            width="10"
            height="6"
            viewBox="0 0 10 6"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={`toggle-icon ${isExpanded ? 'is-open' : ''}`}
          >
            <path
              d="M1 1L5 5L9 1"
              stroke="#00ADEF"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
      {isExpanded && (
        <div className="group-content">
          <div className="rules-column">
            {leftRules.map(rule => (
              <RuleOption
                key={rule._id}
                rule={rule}
                isChecked={selectedRulesIds.includes(rule._id)}
                onCheck={onCheck}
              />
            ))}
          </div>
          <div className="rules-column">
            {rightRules.map(rule => (
              <RuleOption
                key={rule._id}
                rule={rule}
                isChecked={selectedRulesIds.includes(rule._id)}
                onCheck={onCheck}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const CustomRuleBox = ({ rule, isChecked, isEditing, editValue, setEditValue, onCheck, onEdit, onCancel, onSave, onDelete, statusIcon }) => {
  if (isEditing) {
    return (
      <div className="custom-rule-item">
        <div className="edit-input-wrapper">
          <input
            autoFocus
            placeholder="Enter rules"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSave(rule);
              if (e.key === "Escape") onCancel();
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <div className="edit-btn-group">
            <span
              onClick={(e) => { e.stopPropagation(); onCancel(); }}
              className="text-[#767676] text-[12px] font-[400] font-poppins cursor-pointer hover:underline mr-1"
            >
              Cancel
            </span>
            <span
              onClick={(e) => { e.stopPropagation(); onSave(rule); }}
              className="text-[#00ADEF] text-[12px] font-[500] font-poppins cursor-pointer select-none"
            >
              Save
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`custom-rule-item ${isChecked ? 'selected' : ''}`}>
      <div className="left-section">
        <div className={`checkbox-mini ${isChecked ? 'checked' : ''}`} onClick={() => onCheck(rule)}>
          {isChecked && <WhitePlainTick className="checkmark-pop" />}
        </div>
        <span className="rule-label">{rule.title}</span>
        {statusIcon && <img src={statusIcon} className="w-4 h-4" alt="" />}
      </div>
      <div className="actions" onClick={(e) => e.stopPropagation()}>
        <span onClick={() => onEdit(rule)}>Edit</span>
        <BlueCrossIcon onClick={() => onDelete(rule)} className="delete-x-icon" />
      </div>
    </div>
  );
};


const RulesRegulations = ({
  stepIndex,
  setStepIndex,
  handlePrevious,
  setPayloadData,
  editOfferData,
  isEdit,
  selectedMerchant,
  handleSaveAndExit,
  payload,
}) => {
  const { registerNextAction, setIsNextLoading, setIsNextDisabled } = useOfferFormContext();
  const dispatch = useDispatch();

  const loading = useSelector((state) => state?.offerReducer?.loading);
  const offerData = useSelector((state) => state?.offerReducer?.createdOffer);
  const role = useSelector((state) => state?.authReducer?.tokenData?.user?.role);
  const selectedBusiness = useSelector((state) => state?.merchantReducer?.selectedMerchent);
  const groupedRulesFromRedux = useSelector((state) => state?.offerReducer?.groupedRules);

  const [addClicked, setAddClicked] = useState(false);
  const [allRules, setAllRules] = useState([]);
  const [predefinedGroups, setPredefinedGroups] = useState([]);
  const [customApprovedRules, setCustomApprovedRules] = useState([]);

  // Local interaction state
  const [selectedRules, setSelectedRules] = useState([]);
  const [selectedRulesIds, setSelectedRulesIds] = useState([]);
  const [offerPendingRule, setOfferPendingRule] = useState([]);

  const [newRule, setNewRule] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [deletedRules, setDeletedRules] = useState([]);
  const [hiddenCustomIds, setHiddenCustomIds] = useState([]);
  const [isAddingRule, setIsAddingRule] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({
    custom: true
  });

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  // CRITICAL: Prevent re-initialization once the user starts interacting
  const hasInitializedSync = useRef(false);

  // Prioritize payload (current form state) over saved Redux state or initial Props
  const currentOfferType = payload?.offerType || offerData?.offerType || editOfferData?.offerType;
  const rulesType = currentOfferType === "ONLINE_OFFER" ? "INSTORE_SWOODLE_OFFER" : currentOfferType;

  const merchantIdForQuery = useMemo(() => {
    const adminSelected = (role === "admin" || role === "ADMIN") && selectedMerchant?._id;
    if (adminSelected) return selectedMerchant?._id;

    return payload?.merchantId ||
      selectedMerchant?._id ||
      offerData?.merchant?._id ||
      offerData?.merchantId ||
      editOfferData?.merchant?._id ||
      editOfferData?.merchantId;
  }, [role, selectedMerchant?._id, payload?.merchantId, offerData?.merchant?._id, offerData?.merchantId, editOfferData?.merchant?._id, editOfferData?.merchantId]);

  // Handle store/merchant sync for admin
  useEffect(() => {
    if (role === "admin") {
      const biz =
        selectedMerchant ||
        offerData?.merchant ||
        editOfferData?.merchant ||
        (offerData?.merchantId ? { _id: offerData.merchantId } : null) ||
        (editOfferData?.merchantId ? { _id: editOfferData.merchantId } : null);
      if (biz && (!selectedBusiness || selectedBusiness?._id !== biz?._id)) {
        if (biz._id && (biz.businessName || biz.name)) {
          dispatch(setCurrentBusiness(biz));
        }
      }
    }
  }, [role, selectedMerchant, offerData?.merchant, offerData?.merchantId, editOfferData?.merchant, editOfferData?.merchantId, selectedBusiness, dispatch]);

  const getRulesData = useCallback(async () => {
    if (!rulesType) return;
    try {
      const [groupedRes, customRes] = await Promise.all([
        dispatch(getGroupedRules({ offerType: rulesType, silent: true }))?.unwrap(),
        dispatch(
          getRules(
            merchantIdForQuery
              ? { offerType: rulesType, merchantId: merchantIdForQuery, merchantid: merchantIdForQuery, silent: true }
              : { offerType: rulesType, silent: true }
          )
        )?.unwrap(),
      ]);

      const groups = Array.isArray(groupedRes?.data) ? groupedRes.data : [];
      // Extract all rules from groups for flat lookup
      const allPredefinedRules = groups.reduce((acc, group) => {
        if (Array.isArray(group.rules)) {
          return [...acc, ...group.rules];
        }
        return acc;
      }, []);

      const customCandidates = Array.isArray(customRes?.data) ? customRes.data.filter((r) => r?.title) : [];
      const explicitCustom = customCandidates.filter((r) => r?.merchantId || r?.merchantid || r?.merchant?._id || r?.isCustom === true);

      const predefinedTitles = new Set(allPredefinedRules.map((r) => String(r.title).trim().toLowerCase()));
      const derivedCustom = customCandidates.filter((r) => r.title && !predefinedTitles.has(String(r.title).trim().toLowerCase()));

      // Combine both sources and ensure uniqueness
      const uniqueCustom = uniqueByTitle([...explicitCustom, ...derivedCustom]);

      setPredefinedGroups(groups.filter(g => g.name?.toLowerCase() !== 'other rules'));
      setCustomApprovedRules(uniqueCustom);
      setAllRules([...(allPredefinedRules || []), ...(uniqueCustom || [])]);
      setHasError(false);
    } catch (error) {
      console.error("Error fetching rules:", error);
      setHasError(true);
    }
  }, [rulesType, merchantIdForQuery, dispatch]);

  useEffect(() => {
    if (rulesType) getRulesData();
  }, [rulesType, merchantIdForQuery, getRulesData]);

  // Track if the user has manually interacted with the rules to stop syncing from props
  const isManuallyChanged = useRef(false);

  // INITIALIZATION: Sync rules from offer data/payload
  useEffect(() => {
    // If user already interacted, don't overwrite their selection
    if (isManuallyChanged.current) return;

    if (allRules.length > 0) {
      // Pick source of truth candidate
      const hasPayloadRules = payload && Array.isArray(payload.ruleAndRegulation) && payload.ruleAndRegulation.length > 0;
      const sourceData = hasPayloadRules
        ? payload
        : (offerData?._id ? offerData : editOfferData);

      // If we're editing but the source details aren't ready yet, keep waiting
      if (isEdit && !sourceData && !hasPayloadRules) return;

      const ruleObjects = [];
      const ruleIds = [];
      const pendingSource = [];

      if (sourceData?.ruleAndRegulation) {
        sourceData.ruleAndRegulation.forEach((rule) => {
          const id = typeof rule === "string" ? rule : rule?._id;
          const ruleTitle = typeof rule === "object" ? rule?.title : null;

          // Try to match by ID first
          let matched = allRules.find(r => r._id === id);

          // Fallback: match by title if ID failed (common for predefined rules across environments)
          if (!matched && ruleTitle) {
            matched = allRules.find(r => r.title.toLowerCase() === ruleTitle.toLowerCase());
          }

          // Final Fallback: use the object as-is if it has enough info
          if (!matched && typeof rule === 'object' && rule?.title) {
            matched = rule;
          }

          if (matched) {
            ruleObjects.push(matched);
            ruleIds.push(matched._id);
            if (matched.status === "pending" || matched.status === "rejected") {
              pendingSource.push(matched);
            }
          }
        });
      }

      // Only perform the update if we actually found something or if we haven't initialized yet
      // This prevents flickering or resetting to empty if rules take a moment to load
      if (ruleIds.length > 0 || !hasInitializedSync.current) {
        setSelectedRules(ruleObjects);
        setSelectedRulesIds(ruleIds);
        setOfferPendingRule(pendingSource);

        // Initialize with default closed state for all likely keys
        const initialExpanded = { custom: true };

        if (ruleObjects.length === 0) {
          if (predefinedGroups.length > 0) {
            const firstKey = predefinedGroups[0]._id || predefinedGroups[0].name;
            initialExpanded[firstKey] = true;
          }
        } else {
          // Iterate selected rules and expand their groups if they are predefined
          ruleObjects.forEach(r => {
            const parentGroup = predefinedGroups.find(group =>
              group.rules?.some(pr => pr._id === r._id || pr.title.toLowerCase() === r.title.toLowerCase())
            );
            if (parentGroup) {
              initialExpanded[parentGroup._id || parentGroup.name] = true;
            }
          });

          // Fallback: If after all checks, nothing is open (e.g. only custom rules selected), maybe default to general?
          // User request: "those should be only opened the rest should be closed". 
          // If only custom rules are selected, then NO predefined groups should be open. This is correct as per request.
        }
        setExpandedGroups(initialExpanded);

        // Mark as initialized if we successfully synced data or if we are sure there is no data to sync
        if (ruleIds.length > 0 || (!isEdit && !hasPayloadRules)) {
          hasInitializedSync.current = true;
        }
      }
    }
  }, [allRules, offerData, editOfferData, payload, isEdit, predefinedGroups]);

  const initialValues = { ruleAndRegulation: [] };
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: rulesOfferSchema,
    onSubmit: async (values) => {
      const createPromise = await dispatch(createOffer({
        ruleAndRegulation: selectedRulesIds,
        offerId: isEdit ? editOfferData?._id : offerData?._id,
        merchantId: selectedMerchant?._id,
      }))?.unwrap();
      if (createPromise?.success === true) {
        setStepIndex(stepIndex + 1);
      }
    },
  });

  const { setFieldValue, setFieldTouched, handleSubmit, errors, touched } = formik;

  useEffect(() => {
    const titles = selectedRules.map(r => r.title).filter(Boolean);
    setFieldValue("ruleAndRegulation", titles, false);
  }, [selectedRules, setFieldValue]);

  useEffect(() => {
    // Only call setPayloadData if we have actually initialized or changed something
    if (hasInitializedSync.current || isManuallyChanged.current) {
      setPayloadData({
        ruleAndRegulation: selectedRulesIds,
        id: offerData?._id || editOfferData?._id,
        merchantId: merchantIdForQuery || selectedMerchant?._id || null,
        isCreated: false,
      });
    }
  }, [selectedRulesIds, offerData?._id, editOfferData?._id, merchantIdForQuery, selectedMerchant?._id, setPayloadData]);

  const attemptNext = async () => {
    formik.setTouched({ ruleAndRegulation: true });
    const validationErrors = await formik.validateForm();
    if (Object.keys(validationErrors || {}).length > 0) {
      scrollToFirstError(validationErrors, ["ruleAndRegulation"]);
      return;
    }
    formik.handleSubmit();
  };

  useEffect(() => { registerNextAction(() => attemptNext()); }, [registerNextAction, selectedRulesIds]);
  useEffect(() => { setIsNextLoading(loading); }, [loading, setIsNextLoading]);

  // Update disabled state for Next button
  useEffect(() => {
    setIsNextDisabled(selectedRulesIds.length === 0);
  }, [selectedRulesIds, setIsNextDisabled]);

  // Interaction handlers
  const handleRuleChecked = useCallback((rule) => {
    isManuallyChanged.current = true;
    const targetId = rule._id;
    setSelectedRulesIds(prev => {
      const isChecked = prev.includes(targetId);
      if (isChecked) {
        setSelectedRules(curr => curr.filter(r => r._id !== targetId));
        return prev.filter(id => id !== targetId);
      } else {
        setSelectedRules(curr => [...curr, rule]);
        return [...prev, targetId];
      }
    });
  }, []);

  const handleEditClick = (rule) => {
    if (!rule) return;
    setEditingId(rule._id);
    setEditValue(rule.title);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const handleSaveEdit = (rule) => {
    if (!editValue.trim()) return;
    isManuallyChanged.current = true;
    const update = (list) => list.map(r => r._id === rule._id ? { ...r, title: editValue } : r);
    setAllRules(prev => update(prev));
    setCustomApprovedRules(prev => update(prev));
    setOfferPendingRule(prev => update(prev));
    setSelectedRules(prev => update(prev));
    setEditingId(null);
  };

  const handleDeleteClick = (rule) => {
    isManuallyChanged.current = true;
    // Dispatch DELETE API call
    dispatch(deleteRule({ id: rule._id, merchantId: merchantIdForQuery }));

    const filter = (list) => list.filter(r => r._id !== rule._id);
    setAllRules(prev => filter(prev));
    setCustomApprovedRules(prev => filter(prev));
    setOfferPendingRule(prev => filter(prev));
    setSelectedRules(prev => filter(prev));
    setSelectedRulesIds(prev => prev.filter(id => id !== rule._id));

    // Track as hidden/deleted for duplicate check logic to prevent 404s on re-delete
    setHiddenCustomIds(prev => [...prev, rule._id]);

    // Store in deleted list - strictly for reference, creation logic will prefer new API call
    setDeletedRules(prev => [...prev, rule]);
  };

  const handleNewRuleSubmit = async () => {
    const title = (newRule || "").trim();
    if (title === "") return;

    const normToCreate = title.toLowerCase();

    // 1. Check if it's in the 'deleted' list
    const deletedMatch = deletedRules.find(r => r.title.toLowerCase() === normToCreate);
    if (deletedMatch) {
      // It was deleted (and removed from server). Clean up local reference and create FRESH.
      setDeletedRules(prev => prev.filter(r => r._id !== deletedMatch._id));
    }

    // 2. Check if it's already in the main lists but just not selected
    // Ignore items that are currently hidden/deleted
    const existingMatch = allRules
      .filter(r => !hiddenCustomIds.includes(r._id))
      .find(r => r.title.toLowerCase() === normToCreate);

    if (existingMatch) {
      isManuallyChanged.current = true;
      if (!selectedRulesIds.includes(existingMatch._id)) {
        setSelectedRules(prev => [...prev, existingMatch]);
        setSelectedRulesIds(prev => [...prev, existingMatch._id]);
      }
      setNewRule("");
      setAddClicked(false);
      return;
    }

    isManuallyChanged.current = true;
    setIsAddingRule(true);
    try {
      const res = await dispatch(createRule({ title, offerId: offerData?._id, offerType: rulesType, merchantId: merchantIdForQuery }))?.unwrap();
      if (res?.success === true) {
        const data = res?.data;

        const cleanUpdate = (prevList) => {
          const list = Array.isArray(prevList) ? prevList : [];
          // Remove any ghost items (same name + hidden) to prevent duplicates
          return [
            ...list.filter(r => !(hiddenCustomIds.includes(r._id) && r.title.toLowerCase() === title.toLowerCase())),
            data
          ];
        };

        setAllRules(cleanUpdate);
        if (role === "admin") setCustomApprovedRules(cleanUpdate);
        else setOfferPendingRule(cleanUpdate);

        setSelectedRules(prev => [...prev, data]);
        setSelectedRulesIds(prev => [...prev, data._id]);
        setNewRule("");
        setAddClicked(false);
      }
    } catch (error) {
      console.error("Error creating custom rule:", error);
    } finally {
      setIsAddingRule(false);
    }
  };

  const allCustomItems = useMemo(() => {
    const items = [...(customApprovedRules || []), ...(offerPendingRule || [])];
    const seen = new Set();
    const out = [];
    items.forEach(item => {
      if (item?._id && !seen.has(item._id)) {
        seen.add(item._id);
        out.push(item);
      }
    });
    return out;
  }, [customApprovedRules, offerPendingRule]);

  return (
    <div className="w-[95%] flex flex-col items-start offer-rules-step pl-4 md:pl-10">
      <div className="w-full relative mt-20">
        <div className="absolute top-[-85px] right-[-25px] md:right-[-60px]">
          <button onClick={handleSaveAndExit} className="px-6 h-[40px] border border-[#00ADEF] rounded-[5px] text-[#00ADEF] text-[14px] font-[500] font-poppins hover:bg-[#F0FBFF] transition-all">
            Save and Exit
          </button>
        </div>

        <div className="w-full mb-6" data-field="ruleAndRegulation">
          <div className="w-full mb-6 relative">
            <h2 className="text-[32px] font-[600] text-[#303030] font-poppins">
              Set Your Offer <span className="text-[#00ADEF]">Rules & Regulations</span>
            </h2>
            <p className="text-[#767676] text-[16px] font-[400] font-poppins mt-0">
              Let customers know how and when they can use your offer.
            </p>
            {selectedRulesIds.length > 0 && (
              <div className="rules-header-right">
                {selectedRulesIds.length} Selected
              </div>
            )}
          </div>

          {!rulesType ? (
            <div className="bg-[#F6FBFE] border border-[#CDEFFB] text-[#086788] rounded-[10px] p-6 text-sm font-poppins flex items-center gap-3">
              <InfoIcon1 className="w-5 h-5 flex-shrink-0" />
              Please select an offer type in the "Create Offer" step to view the suggested Rules & Regulations.
            </div>
          ) : hasError ? (
            <div className="flex flex-col items-center justify-center py-10 border border-dashed border-[#FFD6D6] rounded-[10px] bg-[#FFF5F5]">
              <p className="text-[#D32F2F] font-poppins text-[14px] font-[500]">
                Something Went Wrong
              </p>
              <p className="text-[#B0B0B0] font-poppins text-[12px] mt-2">
                Unable to load rules. Please try again.
              </p>
            </div>
          ) : allRules.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 border border-dashed border-[#D9D9D9] rounded-[10px] bg-[#FAFAFA]">
              <p className="text-[#767676] font-poppins text-[14px]">
                No Rules & Regulations Found
              </p>
            </div>
          ) : (
            <>
              <div className="w-full flex flex-col gap-3">
                {predefinedGroups.map(group => (
                  <GroupCard
                    key={group._id || group.name}
                    id={group._id || group.name}
                    title={group.name}
                    rules={group.rules}
                    icon={group.icon}
                    selectedRulesIds={selectedRulesIds}
                    onCheck={handleRuleChecked}
                    isExpanded={expandedGroups[group._id || group.name]}
                    onToggle={toggleGroup}
                  />
                ))}
              </div>

              <div className="rules-separator">
                <div className="line"></div><span>Or</span><div className="line"></div>
              </div>

              {(allCustomItems.length > 0 || addClicked) && (
                <div className={`custom-rules-container mb-6 ${expandedGroups.custom ? 'is-expanded' : ''}`}>
                  <div className="custom-rules-header" onClick={() => toggleGroup('custom')}>
                    <label className="custom-rules-label">
                      <CustomRulesIcon className="custom-rules-icon" />
                      Custom Rules & Regulations
                    </label>
                    <div className="header-right">
                      <svg
                        width="10"
                        height="6"
                        viewBox="0 0 10 6"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className={`toggle-icon ${expandedGroups.custom ? 'is-open' : ''}`}
                      >
                        <path
                          d="M1 1L5 5L9 1"
                          stroke="#00ADEF"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                  {expandedGroups.custom && (
                    <div className="custom-rules-grid-wrapper">
                      <div className="custom-rules-grid">
                        {allCustomItems.map(rule => (
                          <CustomRuleBox
                            key={rule._id}
                            rule={rule}
                            isChecked={selectedRulesIds.includes(rule._id)}
                            isEditing={editingId === rule._id}
                            editValue={editValue}
                            setEditValue={setEditValue}
                            onCheck={handleRuleChecked}
                            onEdit={handleEditClick}
                            onCancel={handleCancelEdit}
                            onSave={handleSaveEdit}
                            onDelete={handleDeleteClick}
                            statusIcon={rule.status === 'pending' ? pendingIcon : (rule.status === 'rejected' ? declinedIcon : null)}
                          />
                        ))}


                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="add-custom-rule-card mt-6">
                <div className="card-header flex items-center gap-2 mb-3">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 19H21" stroke="#00ADEF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M17 3.0003C17.2652 2.73509 17.6249 2.58612 18 2.58612C18.3751 2.58612 18.7348 2.73509 19 3.0003C19.2652 3.26551 19.4142 3.62521 19.4142 4.0003C19.4142 4.37538 19.2652 4.73509 19 5.0003L7 17.0003L3 18.0003L4 14.0003L16 2.0003H17Z" stroke="#00ADEF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="text-[16px] font-[600] text-[#303030] font-poppins">Add a Custom Rule</span>
                </div>

                <div className="card-body flex items-center gap-3">
                  <div className="flex-1">
                    <input
                      type="text"
                      className="w-full h-[45px] pl-4 pr-3 border border-none rounded-[8px] text-[14px] font-poppins outline-none focus:ring-0 placeholder-[#ADADAD]"
                      placeholder="Type your custom rule here..."
                      value={newRule}
                      onChange={(e) => setNewRule(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleNewRuleSubmit();
                        }
                      }}
                    />
                  </div>
                  <button
                    className="h-[45px] px-6 bg-[#00ADEF] text-white rounded-[8px] font-poppins text-[14px] font-[500] hover:bg-[#009BD6] transition-colors flex items-center justify-center whitespace-nowrap min-w-[100px]"
                    onClick={handleNewRuleSubmit}
                    disabled={!newRule.trim() || isAddingRule || allRules.filter(r => !hiddenCustomIds.includes(r._id)).some(r => r.title.toLowerCase() === newRule.trim().toLowerCase())}
                  >
                    {isAddingRule ? (
                      <>
                        <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Adding...
                      </>
                    ) : (
                      'Add +'
                    )}
                  </button>
                </div>

                {/* Validation Error Display */}
                {(() => {
                  const normNew = (newRule || "").trim().toLowerCase();
                  if (!normNew) return null;
                  const exists = allRules
                    .filter(r => !hiddenCustomIds.includes(r._id))
                    .some(r => r.title.toLowerCase() === normNew);
                  if (exists) {
                    return (
                      <div className="mt-2 ml-1">
                        <span className="text-red-500 text-[12px] font-[500]">
                          This rule already exists.
                        </span>
                      </div>
                    );
                  }
                  return null;
                })()}

                <div className="card-footer mt-2">
                  <span className="text-[12px] text-[#ADADAD] italic font-poppins">
                    Custom rules will be saved for future offers.
                  </span>
                </div>
              </div>

            </>
          )}

          {
            errors.ruleAndRegulation && touched.ruleAndRegulation && (
              <div className="text-red-600 text-[11px] font-poppins mt-6">{errors.ruleAndRegulation}</div>
            )
          }
        </div >
      </div >
    </div >
  );
};

export default RulesRegulations;
